/*
 * Points de vente — recherche, filtre par pays, tri par distance.
 *
 * Le Liquid rend l'annuaire complet, groupé par pays : ce script ne fait que
 * masquer, et réordonner en mode « proches ». Trois façons d'y arriver :
 *   - taper : le texte filtre nom, ville, code postal, pays et rue, à la volée ;
 *   - valider une recherche qui ne trouve rien : on situe le lieu tapé via la
 *     Base Adresse Nationale (France seulement, sans clé) et on trie les
 *     boutiques par distance — « Lorient » montre Vannes plutôt que rien ;
 *   - « Autour de moi » : la géolocalisation du navigateur, même tri.
 *
 * En mode proches, les fiches quittent leurs groupes pour une liste unique ;
 * l'ordre d'origine est gardé pour les y remettre. La recherche et le pays
 * vivent dans l'URL (?q=, ?pays=), pour qu'un lien partagé rouvre la même vue.
 */
if (!customElements.get('lpf-points-de-vente')) {
  const normaliser = (texte) =>
    (texte || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[’'.,-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const distanceKm = (a, b) => {
    const rad = (d) => (d * Math.PI) / 180;
    const dLat = rad(b.lat - a.lat);
    const dLng = rad(b.lng - a.lng);
    const h =
      Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 6371 * 2 * Math.asin(Math.sqrt(h));
  };

  const formatKm = (km) =>
    km < 10
      ? `${km.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} km`
      : `${Math.round(km).toLocaleString('fr-FR')} km`;

  const pluriel = (n) => `${n} boutique${n > 1 ? 's' : ''}`;

  customElements.define(
    'lpf-points-de-vente',
    class LpfPointsDeVente extends HTMLElement {
      connectedCallback() {
        this.champ = this.querySelector('[data-pdv-champ]');
        this.selectPays = this.querySelector('[data-pdv-pays]');
        this.boutonProche = this.querySelector('[data-pdv-proche]');
        this.boutonEffacer = this.querySelector('[data-pdv-effacer]');
        this.compte = this.querySelector('[data-pdv-compte]');
        this.message = this.querySelector('[data-pdv-message]');
        this.proches = this.querySelector('[data-pdv-proches]');
        this.listeProches = this.querySelector('[data-pdv-proches-liste]');
        this.groupes = [...this.querySelectorAll('[data-pdv-groupe]')];

        this.boutiques = [...this.querySelectorAll('[data-pdv-boutique]')].map((el) => ({
          el,
          parent: el.parentNode,
          texte: normaliser(el.dataset.recherche),
          pays: el.dataset.pays,
          position: el.dataset.lat ? { lat: Number(el.dataset.lat), lng: Number(el.dataset.lng) } : null,
          distance: el.querySelector('[data-pdv-distance]'),
        }));

        // L'origine du tri par distance : { lat, lng, libelle } ou null.
        this.origine = null;

        const params = new URLSearchParams(window.location.search);
        this.champ.value = params.get('q') || '';
        this.selectPays.value = params.get('pays') || '';
        if (this.selectPays.selectedIndex < 0) this.selectPays.value = '';

        this.querySelector('[data-pdv-outils]').hidden = false;

        this.champ.addEventListener('input', () => {
          this.origine = null;
          this.appliquer();
        });
        this.querySelector('[data-pdv-form]').addEventListener('submit', (event) => {
          event.preventDefault();
          this.champ.blur();
          this.chercherLieu();
        });
        this.selectPays.addEventListener('change', () => this.appliquer());
        this.boutonProche.addEventListener('click', () => this.autourDeMoi());
        this.boutonEffacer.addEventListener('click', () => this.effacer());

        this.appliquer();
        if (this.champ.value && this.visibles === 0) this.chercherLieu();
      }

      appliquer() {
        const termes = normaliser(this.champ.value).split(' ').filter(Boolean);
        const pays = this.selectPays.value;
        this.dire('');

        if (this.origine) {
          this.afficherProches(pays);
        } else {
          this.remettreEnGroupes();
          let visibles = 0;
          for (const b of this.boutiques) {
            const ok = (!pays || b.pays === pays) && termes.every((t) => b.texte.includes(t));
            b.el.hidden = !ok;
            b.distance.hidden = true;
            if (ok) visibles += 1;
          }
          this.visibles = visibles;
          this.majGroupes();

          if (visibles === 0 && termes.length) {
            this.dire((this.dataset.vide || '').replace('[recherche]', this.champ.value.trim()));
          }
          this.compte.textContent = termes.length || pays ? pluriel(visibles) : '';
        }

        // « Autour de moi » est enfoncé quand l'origine est la position du visiteur.
        this.boutonProche.setAttribute('aria-pressed', String(Boolean(this.origine && this.origine.libelle === null)));
        this.boutonEffacer.hidden = !(this.champ.value || pays || this.origine);
        this.majUrl();
      }

      afficherProches(pays) {
        const liste = this.boutiques
          .filter((b) => b.position && (!pays || b.pays === pays))
          .map((b) => ({ b, km: distanceKm(this.origine, b.position) }))
          .sort((x, y) => x.km - y.km);

        for (const b of this.boutiques) b.el.hidden = true;
        for (const { b, km } of liste) {
          b.el.hidden = false;
          b.distance.textContent = formatKm(km);
          b.distance.hidden = false;
          this.listeProches.appendChild(b.el);
        }

        this.visibles = liste.length;
        this.proches.hidden = false;
        for (const groupe of this.groupes) groupe.hidden = true;

        this.compte.textContent = `${pluriel(liste.length)}, de la plus proche à la plus lointaine`;
        if (this.origine.libelle) this.dire(`Les boutiques les plus proches de ${this.origine.libelle}.`);
      }

      // Remet chaque fiche dans son groupe, dans l'ordre du Liquid.
      remettreEnGroupes() {
        if (this.proches.hidden) return;
        for (const b of this.boutiques) b.parent.appendChild(b.el);
        this.proches.hidden = true;
      }

      majGroupes() {
        for (const groupe of this.groupes) {
          const n = groupe.querySelectorAll('[data-pdv-boutique]:not([hidden])').length;
          groupe.hidden = n === 0;
          groupe.querySelector('[data-pdv-nombre]').textContent = pluriel(n);
        }
      }

      // Une recherche validée sans résultat : on situe le lieu tapé.
      async chercherLieu() {
        const q = this.champ.value.trim();
        if (!q || this.visibles > 0) return;

        try {
          const url = `https://api-adresse.data.gouv.fr/search/?limit=1&q=${encodeURIComponent(q)}`;
          const reponse = await fetch(url);
          const lieu = reponse.ok ? (await reponse.json()).features?.[0] : null;
          if (!lieu || lieu.properties.score < 0.4) return;
          const [lng, lat] = lieu.geometry.coordinates;
          this.origine = { lat, lng, libelle: lieu.properties.city || lieu.properties.label };
          this.appliquer();
        } catch (erreur) {
          // Hors ligne ou service indisponible : le message « aucune boutique » reste.
        }
      }

      autourDeMoi() {
        if (!navigator.geolocation) {
          this.dire(this.dataset.erreurGeo);
          return;
        }
        this.boutonProche.setAttribute('aria-busy', 'true');
        navigator.geolocation.getCurrentPosition(
          ({ coords }) => {
            this.boutonProche.removeAttribute('aria-busy');
            this.champ.value = '';
            this.origine = { lat: coords.latitude, lng: coords.longitude, libelle: null };
            this.appliquer();
          },
          () => {
            this.boutonProche.removeAttribute('aria-busy');
            this.dire(this.dataset.erreurGeo);
          },
          { timeout: 10000, maximumAge: 600000 }
        );
      }

      effacer() {
        this.champ.value = '';
        this.selectPays.value = '';
        this.origine = null;
        this.appliquer();
        this.champ.focus();
      }

      dire(texte) {
        this.message.textContent = texte;
        this.message.hidden = !texte;
      }

      majUrl() {
        const url = new URL(window.location.href);
        const q = this.champ.value.trim();
        if (q) url.searchParams.set('q', q);
        else url.searchParams.delete('q');
        if (this.selectPays.value) url.searchParams.set('pays', this.selectPays.value);
        else url.searchParams.delete('pays');
        window.history.replaceState(null, '', url);
      }
    }
  );
}
